import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import '@brand/overrides.css';
import App from './App';

import { getI18nService } from '@infra/i18n';
import { formatService } from '@infra/format';
import { initThemeRuntime, initThemeSyncListener } from '@services/theme';
import { initCrossTabAuthSync } from '@services/auth';
import { initLanguageSync } from '@services/i18n';
import { PRE_RENDER_DELAYS_MS } from './app/constants/bootstrap';

initThemeRuntime();
initThemeSyncListener(); // Listen for theme changes across tabs
initCrossTabAuthSync(); // Tear down when another tab logs out / is de-authed
initLanguageSync(); // Persist in-app language changes to the backend (DB + FusionAuth)

const i18nService = getI18nService();

 // ≤ ~1.3s added to first paint on total failure

const initI18nBeforePaint = async (): Promise<boolean> => {
  for (let i = 0; i < PRE_RENDER_DELAYS_MS.length; i++) {
    if (PRE_RENDER_DELAYS_MS[i]) {
      await new Promise((resolve) => setTimeout(resolve, PRE_RENDER_DELAYS_MS[i]));
    }
    try {
      await i18nService.initialize();
      return true;
    } catch (error) {
      console.error(`i18n init attempt ${i + 1}/${PRE_RENDER_DELAYS_MS.length} failed:`, error);
    }
  }
  return false;
};

const initI18nInBackground = (): void => {
  let delay = 3000;
  const tick = () => {
    i18nService
      .initialize()
      .then(() => console.info('i18n recovered on a background retry'))
      .catch(() => {
        delay = Math.min(delay * 2, 30000);
        setTimeout(tick, delay);
      });
  };
  setTimeout(tick, delay);
};

const startApp = async () => {
  const ready = await initI18nBeforePaint();
  if (!ready) initI18nInBackground();

  // Single source of truth for locale: align the format service (dates/numbers/currency)
  // with the i18n language at boot. Both detect from localStorage['language'] but with
  // different fallbacks, so without this they can diverge when no preference was ever
  // persisted. `persist=false` so this alignment does NOT write localStorage — a
  // detected language is not a choice the reader made. `setupI18nSync` keeps them
  // aligned on subsequent switches. `getLanguage()` is valid even if initialize() failed.
  formatService.setLocale(i18nService.getLanguage(), false);

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
};

startApp();
